import { resolveAIProfile } from "@x12i/ai-profiles";
import { requireProfileResolveKey } from "./profileResolveKey.js";
import type {
  AIProfileBackend,
  InstructionTier,
  ProfileCatalogLane,
  RegistrySourceMode,
  ResolvedAIProfile,
} from "@x12i/ai-profiles";

export type ResolveProfileForAskInput = {
  profile: string;
  /** OpenRouter catalog lane — must match the profile (e.g. `text`, `image`). */
  catalogLane: ProfileCatalogLane;
  choice?: string;
  source?: RegistrySourceMode;
  refresh?: boolean;
};

/** FuncX / ask-node model config derived from {@link ResolvedAIProfile}. */
export type AskProfileModelConfig = {
  provider: ResolvedAIProfile["provider"];
  model: string;
  instructionTier: InstructionTier;
  backend: AIProfileBackend;
  profile: string;
  choice: string;
  temperature: number;
  outputMode: ResolvedAIProfile["runtime"]["outputMode"];
  toolPolicy: ResolvedAIProfile["runtime"]["toolPolicy"];
  reasoningEffort: ResolvedAIProfile["runtime"]["reasoningEffort"];
  executionMode: ResolvedAIProfile["runtime"]["executionMode"];
  requireCitations: boolean;
  allowWeb: boolean;
  allowFileSearch: boolean;
  allowCodeExecution: boolean;
  humanReview: ResolvedAIProfile["runtime"]["humanReview"];
  metadata?: Record<string, unknown>;
  pricing?: ResolvedAIProfile["pricing"];
  resolved: ResolvedAIProfile;
};

export function profileConfigFromResolved(
  resolved: ResolvedAIProfile,
): AskProfileModelConfig {
  return {
    provider: resolved.provider,
    model: resolved.modelId,
    instructionTier: resolved.instructionTier,
    backend: resolved.backend,
    profile: resolved.profile,
    choice: resolved.choice,
    temperature: resolved.runtime.temperature,
    outputMode: resolved.runtime.outputMode,
    toolPolicy: resolved.runtime.toolPolicy,
    reasoningEffort: resolved.runtime.reasoningEffort,
    executionMode: resolved.runtime.executionMode,
    requireCitations: resolved.runtime.requireCitations,
    allowWeb: resolved.runtime.allowWeb,
    allowFileSearch: resolved.runtime.allowFileSearch,
    allowCodeExecution: resolved.runtime.allowCodeExecution,
    humanReview: resolved.runtime.humanReview,
    ...(resolved.metadata !== undefined ? { metadata: resolved.metadata } : {}),
    ...(resolved.pricing !== undefined ? { pricing: resolved.pricing } : {}),
    resolved,
  };
}

/**
 * Resolve a profile (+ optional choice) into ask-node / FuncX model configuration.
 * Uses {@link ResolvedAIProfile.instructionTier} and {@link ResolvedAIProfile.backend}
 * from `@x12i/ai-profiles` — do not duplicate preset tables locally.
 */
export async function resolveProfileForAsk(
  input: ResolveProfileForAskInput,
): Promise<AskProfileModelConfig> {
  const refresh =
    input.refresh ??
    (input.source !== undefined && input.source !== "auto" ? true : undefined);
  const key = await requireProfileResolveKey(input.profile, {
    source: input.source,
    refresh,
    choice: input.choice,
  });
  const resolved = await resolveAIProfile(key, {
    catalogLane: input.catalogLane,
    source: input.source,
    refresh,
  });
  return profileConfigFromResolved(resolved);
}
